import { Controller } from "@hotwired/stimulus";

declare global {
  interface Window {
    toast?: (message: string, options?: { type?: string }) => void;
  }
}

type RowCompetition = { id: string; title: string; current: number; default: number };
type Mode = "cell" | "row";

export default class extends Controller {
  static values = { url: String };
  static targets = [
    "popover",
    "title",
    "summary",
    "count",
    "countSuffix",
    "onlyEmptyWrap",
    "onlyEmpty",
    "submit",
    "submitLabel",
  ];

  declare urlValue: string;
  declare popoverTarget: HTMLElement;
  declare titleTarget: HTMLElement;
  declare summaryTarget: HTMLElement;
  declare countTarget: HTMLInputElement;
  declare countSuffixTarget: HTMLElement;
  declare onlyEmptyWrapTarget: HTMLElement;
  declare onlyEmptyTarget: HTMLInputElement;
  declare submitTarget: HTMLButtonElement;
  declare submitLabelTarget: HTMLElement;

  private mode: Mode = "cell";
  private modelId = "";
  private modelName = "";
  private cellCompetitionId = "";
  private cellCompetitionTitle = "";
  private cellCurrentCount = 0;
  private rowCompetitions: RowCompetition[] = [];
  private outsideClickHandler = (e: MouseEvent) => this.onOutsideClick(e);
  private escHandler = (e: KeyboardEvent) => {
    if (e.key === "Escape") this.close();
  };

  openCell(event: Event) {
    event.preventDefault();
    const btn = event.currentTarget as HTMLElement;
    this.mode = "cell";
    this.modelId = btn.dataset.matrixGeneratorModelIdParam || "";
    this.modelName = btn.dataset.matrixGeneratorModelNameParam || "";
    this.cellCompetitionId = btn.dataset.matrixGeneratorCompetitionIdParam || "";
    this.cellCompetitionTitle = btn.dataset.matrixGeneratorCompetitionTitleParam || "";
    this.cellCurrentCount = parseInt(btn.dataset.matrixGeneratorCurrentCountParam || "0", 10);
    const defaultCount = parseInt(btn.dataset.matrixGeneratorDefaultCountParam || "3", 10);

    this.titleTarget.textContent = this.cellCurrentCount > 0 ? "Add more competitors" : "Add competitors";
    this.countTarget.value = String(defaultCount);
    this.countSuffixTarget.textContent = "to add";
    this.onlyEmptyWrapTarget.classList.add("hidden");
    this.onlyEmptyWrapTarget.classList.remove("flex");

    this.refreshSummary();
    this.show(btn);
  }

  openRow(event: Event) {
    event.preventDefault();
    const btn = event.currentTarget as HTMLElement;
    this.mode = "row";
    this.modelId = btn.dataset.matrixGeneratorModelIdParam || "";
    this.modelName = btn.dataset.matrixGeneratorModelNameParam || "";
    try {
      this.rowCompetitions = JSON.parse(btn.dataset.matrixGeneratorCompetitionsParam || "[]");
    } catch {
      this.rowCompetitions = [];
    }

    // Refresh `current` counts from the live DOM so multi-shot row fills stay accurate.
    this.rowCompetitions = this.rowCompetitions.map((c) => {
      const cellBtn = document.querySelector(
        `#matrix_cell_${this.modelId}_${c.id} [data-matrix-generator-current-count-param]`,
      ) as HTMLElement | null;
      const fresh = cellBtn
        ? parseInt(cellBtn.dataset.matrixGeneratorCurrentCountParam || "0", 10)
        : c.current;
      return { ...c, current: fresh };
    });

    this.titleTarget.textContent = "Fill row";
    this.countTarget.value = "3";
    this.countSuffixTarget.textContent = "per competition";
    this.onlyEmptyWrapTarget.classList.remove("hidden");
    this.onlyEmptyWrapTarget.classList.add("flex");
    this.onlyEmptyTarget.checked = true;

    this.refreshSummary();
    this.show(btn);
  }

  refreshSummary() {
    const count = Math.max(1, Math.min(10, parseInt(this.countTarget.value || "0", 10) || 0));
    if (this.mode === "cell") {
      const newTotal = this.cellCurrentCount + count;
      this.summaryTarget.innerHTML = `
        <div><span class="text-base-content/50">Model:</span> <strong>${escapeHtml(this.modelName)}</strong></div>
        <div><span class="text-base-content/50">Challenge:</span> <strong>${escapeHtml(this.cellCompetitionTitle)}</strong></div>
        <div><span class="text-base-content/50">Currently:</span> ${this.cellCurrentCount} → <strong>${newTotal}</strong></div>
      `;
      this.submitLabelTarget.textContent = `Generate ${count}`;
    } else {
      const onlyEmpty = this.onlyEmptyTarget.checked;
      const targets = onlyEmpty
        ? this.rowCompetitions.filter((c) => c.current === 0)
        : this.rowCompetitions;
      const total = targets.length * count;
      const list = targets
        .slice(0, 6)
        .map((c) => `<li class="truncate">${escapeHtml(c.title)} <span class="text-base-content/40">(now ${c.current})</span></li>`)
        .join("");
      const overflow = targets.length > 6 ? `<li class="text-base-content/50">…and ${targets.length - 6} more</li>` : "";
      this.summaryTarget.innerHTML = `
        <div><span class="text-base-content/50">Model:</span> <strong>${escapeHtml(this.modelName)}</strong></div>
        <div><span class="text-base-content/50">Will create:</span> <strong>${total}</strong> generations across <strong>${targets.length}</strong> competition${targets.length === 1 ? "" : "s"}</div>
        ${targets.length > 0 ? `<ul class="list-disc pl-4 mt-1 max-h-32 overflow-y-auto">${list}${overflow}</ul>` : `<div class="text-warning">No competitions match — uncheck "only empty" or pick another row.</div>`}
      `;
      this.submitLabelTarget.textContent = total > 0 ? `Generate ${total}` : "Nothing to do";
      this.submitTarget.disabled = total === 0;
    }
  }

  submit() {
    const count = Math.max(1, Math.min(10, parseInt(this.countTarget.value || "0", 10) || 0));

    const competitionIds: string[] =
      this.mode === "cell"
        ? [this.cellCompetitionId]
        : this.onlyEmptyTarget.checked
          ? this.rowCompetitions.filter((c) => c.current === 0).map((c) => c.id)
          : this.rowCompetitions.map((c) => c.id);

    if (competitionIds.length === 0) return;

    // Optimistic UI update — gives instant feedback before the bg job runs.
    this.applyOptimisticUpdate(competitionIds, count);

    const total = competitionIds.length * count;
    window.toast?.(
      `Queued ${total} generation${total === 1 ? "" : "s"} — they'll appear shortly.`,
      { type: "success" },
    );

    this.close();

    const formData = new FormData();
    formData.append("model_id", this.modelId);
    formData.append("count", String(count));
    if (this.mode === "row") {
      formData.append("only_empty", this.onlyEmptyTarget.checked ? "1" : "0");
    }
    competitionIds.forEach((id) => formData.append("competition_ids[]", id));

    fetch(this.urlValue, {
      method: "POST",
      headers: { "X-CSRF-Token": this.csrfToken },
      body: formData,
      credentials: "same-origin",
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
      })
      .catch((err) => {
        console.error("matrix-generator: enqueue failed", err);
        window.toast?.("Failed to queue generation. Please refresh and try again.", { type: "error" });
      });
  }

  private applyOptimisticUpdate(competitionIds: string[], count: number) {
    let totalDelta = 0;

    competitionIds.forEach((compId) => {
      const cell = document.getElementById(`matrix_cell_${this.modelId}_${compId}`);
      if (!cell) return;

      const btn = cell.querySelector<HTMLElement>("[data-matrix-generator-current-count-param]");
      if (!btn) return;

      const cur = parseInt(btn.dataset.matrixGeneratorCurrentCountParam || "0", 10);
      const next = cur + count;

      btn.dataset.matrixGeneratorCurrentCountParam = String(next);
      btn.classList.remove("text-base-content/20");
      btn.classList.add("text-base-content");
      btn.innerHTML = `<span class="badge badge-sm badge-primary badge-soft">${next}</span>`;

      const colTotal = document.getElementById(`matrix_col_total_${compId}`);
      if (colTotal) {
        colTotal.textContent = String((parseInt(colTotal.textContent || "0", 10) || 0) + count);
      }

      totalDelta += count;
    });

    const rowTotal = document.getElementById(`matrix_row_total_${this.modelId}`);
    if (rowTotal) {
      rowTotal.textContent = String((parseInt(rowTotal.textContent || "0", 10) || 0) + totalDelta);
    }

    const grandTotal = document.getElementById("matrix_grand_total");
    if (grandTotal) {
      grandTotal.textContent = String((parseInt(grandTotal.textContent || "0", 10) || 0) + totalDelta);
    }
  }

  close() {
    this.popoverTarget.classList.add("hidden");
    document.removeEventListener("mousedown", this.outsideClickHandler);
    document.removeEventListener("keydown", this.escHandler);
  }

  private show(anchor: HTMLElement) {
    this.popoverTarget.classList.remove("hidden");
    this.position(anchor);
    this.submitTarget.disabled = false;
    setTimeout(() => {
      document.addEventListener("mousedown", this.outsideClickHandler);
      document.addEventListener("keydown", this.escHandler);
      this.countTarget.focus();
      this.countTarget.select();
    }, 0);
  }

  private position(anchor: HTMLElement) {
    const rect = anchor.getBoundingClientRect();
    const pop = this.popoverTarget;
    const popWidth = pop.offsetWidth || 320;
    const popHeight = pop.offsetHeight || 240;
    const margin = 8;

    let left = rect.left + rect.width / 2 - popWidth / 2;
    left = Math.max(margin, Math.min(window.innerWidth - popWidth - margin, left));

    let top = rect.bottom + margin;
    if (top + popHeight > window.innerHeight - margin) {
      top = Math.max(margin, rect.top - popHeight - margin);
    }

    pop.style.left = `${left}px`;
    pop.style.top = `${top}px`;
  }

  private onOutsideClick(event: MouseEvent) {
    if (this.popoverTarget.contains(event.target as Node)) return;
    this.close();
  }

  private get csrfToken(): string {
    const meta = document.querySelector('meta[name="csrf-token"]');
    return meta?.getAttribute("content") || "";
  }
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}
